// The ⌘K command palette — Dourado's global, lightning-fast "jump to anywhere".
//
// Design decisions (grounded in output/dourado-home-and-search-report.md):
//   • Native <dialog>.showModal() — free focus-trap, Escape handling, background
//     `inert`, and focus restoration. "No ARIA is better than bad ARIA": the
//     less custom modal plumbing we hand-roll, the fewer a11y bugs we ship.
//   • APG combobox semantics layered on top: the input is role="combobox" with
//     aria-expanded / aria-controls / aria-activedescendant; the results list is
//     role="listbox" with role="option" rows. DOM focus never leaves the input —
//     the "active" row is virtual, tracked only via aria-activedescendant.
//   • Diacritic-insensitive matching + highlighting via the same fold() the index
//     uses, so "esta" both finds AND visibly highlights "está".
//   • ~130ms debounce: in-memory search returns in <5ms, so this only suppresses
//     per-keystroke flicker while staying well under the ~250ms "feels instant"
//     reaction-time bound.
//   • Empty state offers quick links (Start at A1/A2/B1 + content pillars) so the
//     palette is useful before the user types a single character.
import { navigate } from "../router/router.js";
import {
  buildSearchIndex,
  searchEntries,
  groupResults,
  TYPE_META,
  fold,
} from "../lib/searchIndex.js";
import { escapeHtml, debounce } from "./helpers.js";

let dialog = null;
let input = null;
let listEl = null;
let liveEl = null;
let footerEl = null;

let indexPromise = null; // lazy — built on first open
let index = null;
let flat = []; // current flat, ranked result list (for keyboard nav)
let activeIdx = -1; // index into `flat`; -1 = nothing active
let lastTrigger = null; // element to restore focus to on close

// Quick links shown in the empty state (before the user types).
const QUICK_LINKS = [
  { label: "Start at A1 — Beginner", nav: "article", params: { slug: "ser-vs-estar" }, emoji: "🌱" },
  { label: "Start at A2 — Elementary", nav: "article", params: { slug: "a2-preterito-vs-imperfeito" }, emoji: "🌿" },
  { label: "Start at B1 — Intermediate", nav: "article", params: { slug: "b1-present-subjunctive" }, emoji: "🌳" },
  { label: "Browse all verbs", nav: "search", params: {}, emoji: "🔤" },
  { label: "Grammar guide", nav: "guide", params: {}, emoji: "📖" },
  { label: "Vocabulary", nav: "vocabulary", params: {}, emoji: "🗂️" },
  { label: "Common expressions", nav: "expressions", params: {}, emoji: "🎭" },
  { label: "Reference shelf", nav: "reference", params: {}, emoji: "🖼️" },
];

// Fold-aware highlight: find the folded query inside the folded text, then map
// the match back onto the ORIGINAL string so accents render but still highlight.
function highlightFolded(text, qn) {
  const safe = () => escapeHtml(text);
  if (!qn) return safe();
  const folded = fold(text);
  const idx = folded.indexOf(qn);
  if (idx === -1) return safe();
  // fold() preserves length per-character for our corpus (NFD strips zero-width
  // combining marks, but the base letters stay 1:1), so indices line up.
  const before = escapeHtml(text.slice(0, idx));
  const hit = escapeHtml(text.slice(idx, idx + qn.length));
  const after = escapeHtml(text.slice(idx + qn.length));
  return `${before}<mark class="bg-sage-100 text-sage-900 rounded px-0.5">${hit}</mark>${after}`;
}

function ensureDialog() {
  if (dialog) return;

  dialog = document.createElement("dialog");
  dialog.className = "cmdk";
  dialog.setAttribute("aria-label", "Search Dourado");
  dialog.innerHTML = `
    <div class="cmdk-box">
      <div class="cmdk-input-row">
        <span class="cmdk-search-icon" aria-hidden="true">🔍</span>
        <input
          id="cmdk-input"
          type="text"
          class="cmdk-input"
          role="combobox"
          aria-expanded="false"
          aria-controls="cmdk-listbox"
          aria-autocomplete="list"
          aria-activedescendant=""
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          placeholder="Search verbs, grammar, vocabulary, expressions…"
        />
        <kbd class="cmdk-esc">Esc</kbd>
      </div>
      <ul id="cmdk-listbox" class="cmdk-list" role="listbox" aria-label="Search results"></ul>
      <div class="cmdk-footer">
        <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
        <span><kbd>↵</kbd> open</span>
        <span><kbd>Esc</kbd> close</span>
      </div>
      <p id="cmdk-live" class="sr-only" role="status" aria-live="polite"></p>
    </div>
  `;
  document.body.appendChild(dialog);

  input = dialog.querySelector("#cmdk-input");
  listEl = dialog.querySelector("#cmdk-listbox");
  liveEl = dialog.querySelector("#cmdk-live");
  footerEl = dialog.querySelector(".cmdk-footer");

  const runSearch = debounce(() => render(input.value), 130);
  input.addEventListener("input", runSearch);
  input.addEventListener("keydown", onKeydown);

  // Click outside the inner box (on the dialog backdrop area) closes.
  dialog.addEventListener("mousedown", (e) => {
    if (e.target === dialog) close();
  });
  // Native dialog "cancel" (Escape) — restore focus ourselves.
  dialog.addEventListener("close", () => {
    if (lastTrigger && typeof lastTrigger.focus === "function") lastTrigger.focus();
    lastTrigger = null;
  });
  // Delegate row activation.
  listEl.addEventListener("mousedown", (e) => {
    const li = e.target.closest("[data-row]");
    if (!li) return;
    e.preventDefault();
    const i = Number(li.dataset.row);
    if (!Number.isNaN(i)) activate(i);
  });
  listEl.addEventListener("mousemove", (e) => {
    const li = e.target.closest("[data-row]");
    if (!li) return;
    const i = Number(li.dataset.row);
    if (!Number.isNaN(i) && i !== activeIdx) setActive(i);
  });
}

function rowId(i) {
  return `cmdk-row-${i}`;
}

// Render the empty-state quick links into the flat list (so ↑/↓/Enter work).
function renderEmptyState() {
  flat = QUICK_LINKS.map((q) => ({
    type: "page",
    title: q.label,
    subtitle: "",
    nav: q.nav,
    params: q.params,
    _emoji: q.emoji,
    _quick: true,
  }));
  activeIdx = flat.length ? 0 : -1;

  const rows = flat
    .map(
      (r, i) => `
      <li id="${rowId(i)}" data-row="${i}" role="option"
          aria-selected="${i === activeIdx}"
          class="cmdk-row ${i === activeIdx ? "is-active" : ""}">
        <span class="cmdk-row-emoji" aria-hidden="true">${r._emoji || "•"}</span>
        <span class="cmdk-row-main">
          <span class="cmdk-row-title">${escapeHtml(r.title)}</span>
        </span>
        <span class="cmdk-row-type">Jump to</span>
      </li>`
    )
    .join("");

  listEl.innerHTML = `<li class="cmdk-group-label" role="presentation">Quick links</li>${rows}`;
  input.setAttribute("aria-expanded", "true");
  syncActiveDescendant();
  setLive("Type to search. 8 quick links available.");
}

function render(rawQuery) {
  const q = (rawQuery || "").trim();
  // Quick links are static navigation — show them immediately, even before the
  // search index has finished loading.
  if (!q) {
    renderEmptyState();
    return;
  }
  if (!index) {
    listEl.innerHTML = `<li class="cmdk-empty" role="presentation">Loading…</li>`;
    return;
  }

  const results = searchEntries(index, q, 30);
  const qn = fold(q);

  if (!results.length) {
    flat = [];
    activeIdx = -1;
    listEl.innerHTML = `<li class="cmdk-empty" role="presentation">No matches for “${escapeHtml(q)}”.</li>`;
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-activedescendant", "");
    setLive(`No results for ${q}.`);
    return;
  }

  const groups = groupResults(results, 6);

  // Flatten in display order so keyboard nav matches what the eye sees.
  flat = [];
  let html = "";
  for (const g of groups) {
    const meta = TYPE_META[g.type];
    html += `<li class="cmdk-group-label" role="presentation">${meta.emoji} ${escapeHtml(meta.label)}</li>`;
    for (const r of g.items) {
      const i = flat.length;
      flat.push(r);
      const sub = r.subtitle
        ? `<span class="cmdk-row-sub">${highlightFolded(r.subtitle, qn)}</span>`
        : "";
      html += `
        <li id="${rowId(i)}" data-row="${i}" role="option"
            aria-selected="false"
            class="cmdk-row">
          <span class="cmdk-row-emoji" aria-hidden="true">${meta.emoji}</span>
          <span class="cmdk-row-main">
            <span class="cmdk-row-title">${highlightFolded(r.title, qn)}</span>
            ${sub}
          </span>
          <span class="cmdk-row-type">${escapeHtml(meta.label)}</span>
        </li>`;
    }
  }

  listEl.innerHTML = html;
  activeIdx = flat.length ? 0 : -1;
  input.setAttribute("aria-expanded", "true");
  syncActiveDescendant();
  setLive(`${results.length} result${results.length === 1 ? "" : "s"}.`);
}

function setActive(i) {
  if (i === activeIdx) return;
  const prev = activeIdx >= 0 ? listEl.querySelector(`#${rowId(activeIdx)}`) : null;
  if (prev) {
    prev.classList.remove("is-active");
    prev.setAttribute("aria-selected", "false");
  }
  activeIdx = i;
  syncActiveDescendant();
}

function syncActiveDescendant() {
  if (activeIdx < 0) {
    input.setAttribute("aria-activedescendant", "");
    return;
  }
  const el = listEl.querySelector(`#${rowId(activeIdx)}`);
  if (!el) {
    input.setAttribute("aria-activedescendant", "");
    return;
  }
  el.classList.add("is-active");
  el.setAttribute("aria-selected", "true");
  input.setAttribute("aria-activedescendant", rowId(activeIdx));
  el.scrollIntoView({ block: "nearest" });
}

function move(delta) {
  if (!flat.length) return;
  let next = activeIdx + delta;
  if (next < 0) next = flat.length - 1;
  if (next >= flat.length) next = 0;
  setActive(next);
}

function activate(i) {
  const r = flat[i];
  if (!r) return;
  close();
  navigate(r.nav, r.params || {});
}

function onKeydown(e) {
  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      move(1);
      break;
    case "ArrowUp":
      e.preventDefault();
      move(-1);
      break;
    case "Enter":
      e.preventDefault();
      if (activeIdx >= 0) activate(activeIdx);
      break;
    case "Home":
      if (flat.length) {
        e.preventDefault();
        setActive(0);
      }
      break;
    case "End":
      if (flat.length) {
        e.preventDefault();
        setActive(flat.length - 1);
      }
      break;
    // Escape is handled natively by <dialog>.
  }
}

function setLive(msg) {
  if (liveEl) liveEl.textContent = msg;
}

export function openCommandPalette() {
  ensureDialog();
  lastTrigger = document.activeElement;

  // Kick off (or reuse) the lazy index build.
  if (!indexPromise) indexPromise = buildSearchIndex();
  indexPromise
    .then((idx) => {
      index = idx;
      if (dialog.open) render(input.value);
    })
    .catch(() => {
      index = [];
      if (dialog.open) {
        listEl.innerHTML = `<li class="cmdk-empty" role="presentation">Search is unavailable right now.</li>`;
      }
    });

  input.value = "";
  if (!dialog.open) dialog.showModal();
  render(""); // empty-state quick links render immediately
  input.focus();
}

export function close() {
  if (dialog && dialog.open) dialog.close();
}

// Wire the global shortcuts once. ⌘K / Ctrl-K from anywhere, plus "/" as a
// secondary trigger when the user isn't already typing in a field.
export function initCommandPalette() {
  document.addEventListener("keydown", (e) => {
    const k = e.key.toLowerCase();
    if ((e.metaKey || e.ctrlKey) && k === "k") {
      e.preventDefault();
      if (dialog && dialog.open) close();
      else openCommandPalette();
      return;
    }
    if (k === "/" && !isTypingTarget(e.target) && !(dialog && dialog.open)) {
      e.preventDefault();
      openCommandPalette();
    }
  });

  // Delegate any header / in-page trigger button.
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest("[data-cmdk-open]");
    if (trigger) {
      e.preventDefault();
      openCommandPalette();
    }
  });
}

function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable === true
  );
}
